import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { computeCommerceGuide } from './commerceGuideLogic';

export interface OpportunityAssistantSuggestion {
    id: string;
    label: string;
    /** Sent to the model when the user taps the chip. */
    prompt: string;
}

const STATIC_SUGGESTIONS: OpportunityAssistantSuggestion[] = [
    {
        id: 'next-step',
        label: 'Next step',
        prompt: 'What is my next step on this opportunity?',
    },
    {
        id: 'financing',
        label: 'Financing',
        prompt: 'What should I select in Financing Options, and what is still missing?',
    },
    {
        id: 'partner-sync',
        label: 'Partner sync',
        prompt: 'What do I need to complete in Finance Partner Sync for my selected partners?',
    },
    {
        id: 'products',
        label: 'Products',
        prompt: 'Are my opportunity products set up correctly for my financing selection?',
    },
    {
        id: 'quotes',
        label: 'Quotes',
        prompt: 'What should I do next in Quotes for this opportunity?',
    },
    {
        id: 'agreements',
        label: 'Agreements',
        prompt: 'What should I review in Agreements and signed contract status?',
    },
    {
        id: 'change-order',
        label: 'Change order',
        prompt: 'Is there an active change order, and what should I do about it?',
    },
    {
        id: 'summarize',
        label: 'Summarize',
        prompt: 'Summarize this opportunity, what is complete, and what is still missing.',
    },
    {
        id: 'dashboard-tour',
        label: 'This dashboard',
        prompt: 'What am I looking at on this dashboard, and which sections matter most right now?',
    },
];

/** Chips shown when the drawer opens (before the first message). */
export function buildOpportunityAssistantSuggestions(): OpportunityAssistantSuggestion[] {
    const s = useOneButtonStore.getState();
    const guide = computeCommerceGuide({
        selectedFinanceCells: s.selectedFinanceCells,
        financePartnerSyncStatus: s.financePartnerSyncStatus,
        opportunityDetails: s.opportunityDetails,
        dashboardQuotesSummaryLines: s.dashboardQuotesSummaryLines,
        changeOrderDetails: s.changeOrderDetails,
    });

    const rest = STATIC_SUGGESTIONS.filter((item) => item.id !== 'next-step');

    if (guide.section === 'none') {
        return [
            {
                id: 'next-step',
                label: 'Next step',
                prompt: 'What is my next step on this opportunity?',
            },
            ...rest,
        ];
    }

    return [
        {
            id: 'next-step',
            label: 'Next step',
            prompt: 'What is my next step on this opportunity?',
        },
        {
            id: 'guide-hint',
            label: hintLabelForSection(guide.section),
            prompt: `The live guide says: "${guide.message}" — explain this in plain language and what I should click in the app.`,
        },
        ...rest.filter((item) => item.id !== hintIdForSection(guide.section)),
    ].slice(0, 8);
}

function hintLabelForSection(section: string): string {
    switch (section) {
        case 'financing':
            return 'Financing help';
        case 'partnerSync':
            return 'Sync help';
        case 'products':
            return 'Products help';
        case 'quotes':
            return 'Quotes help';
        case 'agreements':
            return 'Agreements help';
        case 'changeOrder':
            return 'Change order';
        default:
            return 'Current focus';
    }
}

function hintIdForSection(section: string): string | null {
    switch (section) {
        case 'financing':
            return 'financing';
        case 'partnerSync':
            return 'partner-sync';
        case 'products':
            return 'products';
        case 'quotes':
            return 'quotes';
        case 'agreements':
            return 'agreements';
        case 'changeOrder':
            return 'change-order';
        default:
            return null;
    }
}
